/**
 * Dados mocados das Reunioes.
 *
 * Referencia temporal do mock: quinta-feira, 6 de agosto de 2026, 09:10.
 * `mtg-revisao-jk` e a mesma reuniao apontada por `liveMeetingId` do canal
 * `ch-ops-jk` em `mocks/chat.ts` — os dois arquivos precisam continuar batendo.
 */

export type MeetingStatus = 'ao-vivo' | 'agendada' | 'encerrada'

/**
 * A lista tem exatamente TRES secoes (protótipo `_shell.html:3509,3536-3541`),
 * e dentro de "Próximas"/"Anteriores" as reunioes ainda sao quebradas por DIA.
 */
export type MeetingSection = 'ao-vivo' | 'proximas' | 'anteriores'

export const MEETING_SECTION_LABEL: Record<MeetingSection, string> = {
  'ao-vivo': 'Ao vivo',
  proximas: 'Próximas',
  anteriores: 'Anteriores',
}

/** Icone e cor do quadradinho da linha, por secao (protótipo `:3538-3540`). */
export const MEETING_SECTION_ICON: Record<MeetingSection, string> = {
  'ao-vivo': 'fas fa-video',
  proximas: 'far fa-calendar-alt',
  anteriores: 'fas fa-history',
}

export function meetingSection(status: MeetingStatus): MeetingSection {
  if (status === 'ao-vivo') return 'ao-vivo'
  return status === 'agendada' ? 'proximas' : 'anteriores'
}

const DIAS_DA_SEMANA = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const

/** "06/08/2026" → "06/08/2026 · quinta-feira" (protótipo `:3503-3507`). */
export function meetingDayLabel(dateLabel: string): string {
  const partes = dateLabel.split('/').map(Number)
  const [dia, mes, ano] = partes
  if (partes.length !== 3 || !dia || !mes || !ano) return dateLabel
  return `${dateLabel} · ${DIAS_DA_SEMANA[new Date(ano, mes - 1, dia).getDay()]}`
}

/** "06/08/2026" → "20260806": chave de ordenacao dos sub-cabecalhos. */
export function meetingDayKey(dateLabel: string): string {
  return dateLabel.split('/').reverse().join('')
}

export type MeetingParticipant = {
  id: string
  name: string
  initials: string
  role: string
  /** `false` mostra o participante como convidado que ainda nao entrou. */
  joined: boolean
  micOn: boolean
  camOn: boolean
}

export type AgendaItem = {
  id: string
  title: string
  owner: string
  minutes: number
  done: boolean
}

export type MeetingActionStatus = 'pendente' | 'em-andamento' | 'concluida'

export type MeetingAction = {
  id: string
  title: string
  owner: string
  dueLabel: string
  status: MeetingActionStatus
}

export type Meeting = {
  id: string
  title: string
  status: MeetingStatus
  /** "06/08/2026" — agrupa a lista por dia. */
  dateLabel: string
  /** "09:30" — ordena as reunioes dentro do dia. */
  timeLabel: string
  /** Ja formatado para leitura: "Hoje, 09:30". */
  whenLabel: string
  durationLabel: string
  organizer: string
  channelId: string | null
  channelName: string | null
  contextCode: string | null
  participants: readonly MeetingParticipant[]
  agenda: readonly AgendaItem[]
  actions: readonly MeetingAction[]
  /** Resumo escrito durante ou depois da reuniao. */
  notes: string | null
}

export const MEETING_STATUS_META: Record<MeetingStatus, { label: string; icon: string }> = {
  'ao-vivo': { label: 'Ao vivo', icon: 'fas fa-circle' },
  agendada: { label: 'Agendada', icon: 'far fa-clock' },
  encerrada: { label: 'Encerrada', icon: 'fas fa-check' },
}

export const ACTION_STATUS_META: Record<MeetingActionStatus, { label: string; icon: string }> = {
  pendente: { label: 'Pendente', icon: 'far fa-circle' },
  'em-andamento': { label: 'Em andamento', icon: 'fas fa-spinner' },
  concluida: { label: 'Concluída', icon: 'fas fa-check-circle' },
}

export const MOCK_MEETINGS: readonly Meeting[] = [
  {
    id: 'mtg-revisao-jk',
    title: 'Revisão de escopo — JK Iguatemi',
    status: 'ao-vivo',
    dateLabel: '06/08/2026',
    timeLabel: '09:30',
    whenLabel: 'Hoje, 09:30',
    durationLabel: '45 min',
    organizer: 'Mariana Bezerra',
    channelId: 'ch-ops-jk',
    channelName: 'Operações — JK Iguatemi',
    contextCode: 'DMD-0047',
    participants: [
      { id: 'p-mb', name: 'Mariana Bezerra', initials: 'MB', role: 'Organizadora', joined: true, micOn: true, camOn: true },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: true, micOn: true, camOn: false },
      { id: 'p-ra', name: 'Rafael Andrade', initials: 'RA', role: 'Infraestrutura', joined: true, micOn: false, camOn: true },
      { id: 'p-jp', name: 'Juliana Prado', initials: 'JP', role: 'Operação', joined: true, micOn: false, camOn: false },
      { id: 'p-cd', name: 'Camila Duarte', initials: 'CD', role: 'Engenharia', joined: false, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-jk-1', title: 'Confirmar a janela de virada com a empresa', owner: 'Mariana Bezerra', minutes: 10, done: true },
      { id: 'ag-jk-2', title: 'Revisar o roteiro de rollback', owner: 'Rafael Andrade', minutes: 15, done: true },
      { id: 'ag-jk-3', title: 'Critérios de aceite pendentes do WI-0186', owner: 'Cristian Ramos', minutes: 12, done: false },
      { id: 'ag-jk-4', title: 'Plano de comunicação com a operação do cliente', owner: 'Juliana Prado', minutes: 8, done: false },
    ],
    actions: [
      { id: 'ac-jk-1', title: 'Confirmar backup do banco com a operação do cliente', owner: 'Rafael Andrade', dueLabel: 'Hoje, 14:00', status: 'em-andamento' },
      { id: 'ac-jk-2', title: 'Publicar o roteiro final da virada no canal', owner: 'Juliana Prado', dueLabel: 'Hoje, 10:00', status: 'pendente' },
      { id: 'ac-jk-3', title: 'Atualizar a demanda DMD-0047 com a janela aprovada', owner: 'Cristian Ramos', dueLabel: 'Ontem, 18:00', status: 'concluida' },
    ],
    notes: 'Janela confirmada das 22h às 02h de sexta-feira. Rollback revisado; falta o aceite do backup.',
  },
  {
    id: 'mtg-daily-plataforma',
    title: 'Daily — Squad Plataforma',
    status: 'agendada',
    dateLabel: '06/08/2026',
    timeLabel: '11:00',
    whenLabel: 'Hoje, 11:00',
    durationLabel: '15 min',
    organizer: 'Camila Duarte',
    channelId: 'ch-squad-plataforma',
    channelName: 'Squad Plataforma',
    contextCode: null,
    participants: [
      { id: 'p-cd', name: 'Camila Duarte', initials: 'CD', role: 'Organizadora', joined: false, micOn: false, camOn: false },
      { id: 'p-tn', name: 'Thiago Nogueira', initials: 'TN', role: 'Engenharia', joined: false, micOn: false, camOn: false },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: false, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-dp-1', title: 'Impedimentos do dia', owner: 'Camila Duarte', minutes: 5, done: false },
      { id: 'ag-dp-2', title: 'Tempo de resposta do bootstrap', owner: 'Thiago Nogueira', minutes: 5, done: false },
      { id: 'ag-dp-3', title: 'Migration de homologação', owner: 'Cristian Ramos', minutes: 5, done: false },
    ],
    actions: [],
    notes: null,
  },
  {
    id: 'mtg-refinamento-pagamentos',
    title: 'Refinamento — Integração de pagamentos',
    status: 'agendada',
    dateLabel: '06/08/2026',
    timeLabel: '16:00',
    whenLabel: 'Hoje, 16:00',
    durationLabel: '1 h',
    organizer: 'Camila Duarte',
    channelId: 'ch-wi-0186',
    channelName: 'WI-0186 · Integração de pagamentos',
    contextCode: 'WI-0186',
    participants: [
      { id: 'p-cd', name: 'Camila Duarte', initials: 'CD', role: 'Organizadora', joined: false, micOn: false, camOn: false },
      { id: 'p-tn', name: 'Thiago Nogueira', initials: 'TN', role: 'Engenharia', joined: false, micOn: false, camOn: false },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: false, micOn: false, camOn: false },
      { id: 'p-lg', name: 'Letícia Gomes', initials: 'LG', role: 'Produto', joined: false, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-rp-1', title: 'Revisar os critérios de aceite do WI-0186', owner: 'Camila Duarte', minutes: 20, done: false },
      { id: 'ag-rp-2', title: 'Cenários de estorno no sandbox do adquirente', owner: 'Thiago Nogueira', minutes: 20, done: false },
      { id: 'ag-rp-3', title: 'Impacto na régua de cobrança', owner: 'Letícia Gomes', minutes: 20, done: false },
    ],
    actions: [],
    notes: null,
  },
  {
    id: 'mtg-comite-portfolio',
    title: 'Comitê de portfólio',
    status: 'agendada',
    dateLabel: '07/08/2026',
    timeLabel: '09:00',
    whenLabel: 'Sexta, 7 de agosto, 09:00',
    durationLabel: '1 h 30 min',
    organizer: 'Letícia Gomes',
    channelId: null,
    channelName: null,
    contextCode: null,
    participants: [
      { id: 'p-lg', name: 'Letícia Gomes', initials: 'LG', role: 'Organizadora', joined: false, micOn: false, camOn: false },
      { id: 'p-mb', name: 'Mariana Bezerra', initials: 'MB', role: 'Operação', joined: false, micOn: false, camOn: false },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: false, micOn: false, camOn: false },
      { id: 'p-eg', name: 'Eduardo Galvão', initials: 'EG', role: 'Diretoria', joined: false, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-cp-1', title: 'Prioridades do trimestre por solução', owner: 'Letícia Gomes', minutes: 40, done: false },
      { id: 'ag-cp-2', title: 'Demandas represadas na triagem', owner: 'Mariana Bezerra', minutes: 30, done: false },
      { id: 'ag-cp-3', title: 'Capacidade dos times para agosto', owner: 'Eduardo Galvão', minutes: 20, done: false },
    ],
    actions: [],
    notes: null,
  },
  {
    id: 'mtg-onboarding-empresas',
    title: 'Onboarding — novos empresas de agosto',
    status: 'agendada',
    dateLabel: '10/08/2026',
    timeLabel: '14:00',
    whenLabel: 'Segunda, 10 de agosto, 14:00',
    durationLabel: '50 min',
    organizer: 'Juliana Prado',
    channelId: null,
    channelName: null,
    contextCode: null,
    participants: [
      { id: 'p-jp', name: 'Juliana Prado', initials: 'JP', role: 'Organizadora', joined: false, micOn: false, camOn: false },
      { id: 'p-ra', name: 'Rafael Andrade', initials: 'RA', role: 'Infraestrutura', joined: false, micOn: false, camOn: false },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: false, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-on-1', title: 'Checklist de ambientes por empresa', owner: 'Rafael Andrade', minutes: 25, done: false },
      { id: 'ag-on-2', title: 'Treinamento das equipes de operação', owner: 'Juliana Prado', minutes: 25, done: false },
    ],
    actions: [],
    notes: null,
  },
  {
    id: 'mtg-retro-julho',
    title: 'Retrospectiva de julho — Squad Plataforma',
    status: 'encerrada',
    dateLabel: '04/08/2026',
    timeLabel: '17:00',
    whenLabel: 'Terça, 4 de agosto, 17:00',
    durationLabel: '1 h',
    organizer: 'Camila Duarte',
    channelId: 'ch-squad-plataforma',
    channelName: 'Squad Plataforma',
    contextCode: null,
    participants: [
      { id: 'p-cd', name: 'Camila Duarte', initials: 'CD', role: 'Organizadora', joined: true, micOn: false, camOn: false },
      { id: 'p-tn', name: 'Thiago Nogueira', initials: 'TN', role: 'Engenharia', joined: true, micOn: false, camOn: false },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: true, micOn: false, camOn: false },
      { id: 'p-lg', name: 'Letícia Gomes', initials: 'LG', role: 'Produto', joined: false, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-rj-1', title: 'O que funcionou bem em julho', owner: 'Camila Duarte', minutes: 20, done: true },
      { id: 'ag-rj-2', title: 'O que atrapalhou a entrega', owner: 'Thiago Nogueira', minutes: 20, done: true },
      { id: 'ag-rj-3', title: 'Compromissos para agosto', owner: 'Camila Duarte', minutes: 20, done: true },
    ],
    actions: [
      { id: 'ac-rj-1', title: 'Reduzir o tempo de revisão de código para 1 dia útil', owner: 'Thiago Nogueira', dueLabel: '14/08', status: 'em-andamento' },
      { id: 'ac-rj-2', title: 'Documentar o processo de migração de banco', owner: 'Cristian Ramos', dueLabel: '12/08', status: 'pendente' },
      { id: 'ac-rj-3', title: 'Revisar o rito de refinamento com produto', owner: 'Camila Duarte', dueLabel: '05/08', status: 'concluida' },
    ],
    notes: 'Time apontou excesso de troca de contexto. Combinado limitar dois itens em andamento por pessoa.',
  },
  {
    id: 'mtg-kickoff-jk',
    title: 'Kickoff — JK Iguatemi',
    status: 'encerrada',
    dateLabel: '03/08/2026',
    timeLabel: '10:00',
    whenLabel: 'Segunda, 3 de agosto, 10:00',
    durationLabel: '1 h 15 min',
    organizer: 'Mariana Bezerra',
    channelId: 'ch-ops-jk',
    channelName: 'Operações — JK Iguatemi',
    contextCode: 'DMD-0047',
    participants: [
      { id: 'p-mb', name: 'Mariana Bezerra', initials: 'MB', role: 'Organizadora', joined: true, micOn: false, camOn: false },
      { id: 'p-jp', name: 'Juliana Prado', initials: 'JP', role: 'Operação', joined: true, micOn: false, camOn: false },
      { id: 'p-ra', name: 'Rafael Andrade', initials: 'RA', role: 'Infraestrutura', joined: true, micOn: false, camOn: false },
      { id: 'p-cr', name: 'Cristian Ramos', initials: 'CR', role: 'Você', joined: true, micOn: false, camOn: false },
    ],
    agenda: [
      { id: 'ag-kj-1', title: 'Escopo contratado e limites da entrega', owner: 'Mariana Bezerra', minutes: 30, done: true },
      { id: 'ag-kj-2', title: 'Ambientes e acessos necessários', owner: 'Rafael Andrade', minutes: 25, done: true },
      { id: 'ag-kj-3', title: 'Calendário de virada', owner: 'Juliana Prado', minutes: 20, done: true },
    ],
    actions: [
      { id: 'ac-kj-1', title: 'Abrir a demanda de virada de ambiente', owner: 'Mariana Bezerra', dueLabel: '04/08', status: 'concluida' },
      { id: 'ac-kj-2', title: 'Levantar os acessos pendentes com a empresa', owner: 'Rafael Andrade', dueLabel: '06/08', status: 'concluida' },
    ],
    notes: 'Escopo aprovado sem alterações. Virada acordada para a primeira sexta com janela noturna.',
  },
]

export function meetingById(id: string | null): Meeting | null {
  if (id === null) return null
  return MOCK_MEETINGS.find((m) => m.id === id) ?? null
}
